import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Alert } from '@mui/material';

import { clearCardDraft } from './cardDraft';
import { focusMayMove, isActionElement } from './cardFocusGuard';
import { isRecord, setPath } from './cardModel';
import { AlertLine, LinkButton } from './cardStyles';
import {
  CardHandlers,
  CardServerError,
  CardSubmitFailure,
  CardSubmitResult,
  FieldErrors,
  FormField,
} from './cardTypes';

export type FocusTarget = 'heading' | 'invalid';

export const SUBMIT_FAILED_TEXT = {
  form: "Couldn't send the form to Alpha. Check your connection, then try again.",
  ask: "Couldn't send your answers to Alpha. Check your connection, then try again.",
  confirm:
    "Couldn't send this to Alpha. Check your connection, then try again.",
};

export const ANOTHER_REQUEST_TEXT = 'Alpha is working on another request.';

export interface CardFailure {
  message: string;
  fieldErrors: FieldErrors;
  unmapped: Array<[string, string]>;
  retry?: 'submit' | 'cancel';
  rejected?: Record<string, unknown>;
}

export interface CardActions {
  busy: boolean;
  cancelling: boolean;
  failure?: CardFailure;
  serverFieldErrors: FieldErrors;
  markEdited: (key: string) => void;
  submit: (values: Record<string, unknown>) => void;
  cancel: () => void;
}

interface CardActionsOptions {
  token: string;
  fields: FormField[];
  serverError?: CardServerError;
  values?: Record<string, unknown>;
  submitting?: boolean;
  cancellingElsewhere?: boolean;
  onSubmit: CardHandlers['onSubmit'];
  onCancel: CardHandlers['onCancel'];
  submitFailedMessage: string;
  cancelFailedMessage: string;
  holdFocus: () => void;
  onServerFailure?: (fieldErrors: FieldErrors, initial: boolean) => void;
}

const INVALID = '[aria-invalid="true"], [data-alpha-invalid="true"]';

export const isSubmitFailure = (
  result: CardSubmitResult,
): result is CardSubmitFailure => !result.ok;

export const addOnce = <T,>(items: T[], item: T): T[] =>
  items.includes(item) ? items : [...items, item];

export const belongsTo = (key: string, fieldKey: string): boolean =>
  key === fieldKey || key.startsWith(`${fieldKey}.`);

export const focusFirstInvalid = (
  root: HTMLElement | null,
  fallback?: HTMLElement | null,
): void => {
  const target = root?.querySelector<HTMLElement>(INVALID);
  if (target == null) {
    return;
  }
  const visible =
    target.getAttribute('aria-hidden') === 'true'
      ? target.parentElement?.querySelector<HTMLElement>('[role="combobox"]')
      : undefined;
  const chosen = visible ?? target;
  (isActionElement(chosen) ? fallback : chosen)?.focus();
};

export const useCardFocus = (
  root: RefObject<HTMLElement>,
  heading: RefObject<HTMLElement>,
): {
  requestFocus: (target: FocusTarget) => void;
  holdFocus: () => void;
} => {
  const [request, setRequest] = useState<
    { target: FocusTarget; seq: number } | undefined
  >();

  useEffect(() => {
    if (request == null || !focusMayMove(root.current)) {
      return;
    }
    if (request.target === 'heading') {
      heading.current?.focus();
      return;
    }
    focusFirstInvalid(root.current, heading.current);
  }, [request, root, heading]);

  const requestFocus = useCallback((target: FocusTarget): void => {
    setRequest(previous => ({ target, seq: (previous?.seq ?? 0) + 1 }));
  }, []);

  const holdFocus = useCallback((): void => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      root.current?.contains(active) === true &&
      focusMayMove(root.current)
    ) {
      heading.current?.focus();
    }
  }, [root, heading]);

  return { requestFocus, holdFocus };
};

const toNested = (flat: Record<string, unknown>): Record<string, unknown> =>
  Object.entries(flat).reduce<Record<string, unknown>>(
    (nested, [key, value]) => setPath(nested, key, value),
    {},
  );

const MULTI_VALUE_TYPES: Array<FormField['type']> = ['multiselect'];
const ROW_INDEX = /^\d+$/;

const readPath = (container: unknown, key: string): unknown =>
  key.split('.').reduce<unknown>((current, segment) => {
    if (Array.isArray(current) && ROW_INDEX.test(segment)) {
      return current[Number(segment)];
    }
    return isRecord(current) ? current[segment] : undefined;
  }, container);

const sameValue = (left: unknown, right: unknown): boolean => {
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((item, index) => sameValue(item, right[index]))
    );
  }
  if (isRecord(left) && isRecord(right)) {
    const keys = Object.keys(left);
    return (
      keys.length === Object.keys(right).length &&
      keys.every(name => sameValue(left[name], right[name]))
    );
  }
  const plain = (value: unknown): unknown => {
    if (typeof value === 'number') {
      return String(value);
    }
    return value === undefined ? null : value;
  };
  return plain(left) === plain(right);
};

const sameSet = (left: unknown[], right: unknown[]): boolean =>
  left.length === right.length &&
  left.every(item => right.some(other => sameValue(item, other))) &&
  right.every(item => left.some(other => sameValue(item, other)));

const isMultiValue = (field: FormField): boolean =>
  field.multiple === true || MULTI_VALUE_TYPES.includes(field.type);

export const rejectedValueHolds = (
  key: string,
  rejected: Record<string, unknown>,
  values: Record<string, unknown>,
  fields: FormField[],
): boolean => {
  const field = fields.find(candidate => belongsTo(key, candidate.key));
  if (field == null) {
    return true;
  }
  const before = readPath(rejected, field.key);
  const now = readPath(values, field.key);
  const sameRows =
    key !== field.key &&
    Array.isArray(before) &&
    Array.isArray(now) &&
    before.length === now.length;
  if (sameRows) {
    return sameValue(readPath(rejected, key), readPath(values, key));
  }
  if (isMultiValue(field) && Array.isArray(before) && Array.isArray(now)) {
    return sameSet(before, now);
  }
  return sameValue(before, now);
};

const plainFailure = (
  message: string,
  retry: 'submit' | 'cancel',
): CardFailure => ({
  message,
  fieldErrors: {},
  unmapped: [],
  retry,
});

export const useCardActions = ({
  token,
  fields,
  serverError,
  values = {},
  submitting = false,
  cancellingElsewhere = false,
  onSubmit,
  onCancel,
  submitFailedMessage,
  cancelFailedMessage,
  holdFocus,
  onServerFailure,
}: CardActionsOptions): CardActions => {
  const [pending, setBusy] = useState(false);
  const [cancelPending, setCancelling] = useState(false);
  const cancelling = cancelPending || cancellingElsewhere;
  const [failure, setFailure] = useState<CardFailure | undefined>();
  const [edited, setEdited] = useState<string[]>([]);
  const latest = useRef({
    fields,
    values,
    onSubmit,
    onCancel,
    onServerFailure,
  });
  const firstRun = useRef(true);
  const busy = pending || submitting;

  useEffect(() => {
    latest.current = { fields, values, onSubmit, onCancel, onServerFailure };
  });

  const applyFailure = useCallback(
    (next: CardServerError, initial: boolean): void => {
      const entries = Object.entries(next.fieldErrors ?? {});
      const known = (key: string): boolean =>
        latest.current.fields.some(field => belongsTo(key, field.key));
      const rejected = next.values != null ? toNested(next.values) : undefined;
      const mapped = Object.fromEntries(entries.filter(([key]) => known(key)));
      const current = Object.fromEntries(
        Object.entries(mapped).filter(
          ([key]) =>
            rejected == null ||
            rejectedValueHolds(
              key,
              rejected,
              latest.current.values,
              latest.current.fields,
            ),
        ),
      );
      setBusy(false);
      setEdited([]);
      setFailure({
        message: next.message,
        fieldErrors: mapped,
        unmapped: entries.filter(([key]) => !known(key)),
        retry: Object.keys(current).length > 0 ? undefined : 'submit',
        rejected,
      });
      latest.current.onServerFailure?.(current, initial);
    },
    [],
  );

  useEffect(() => {
    const initial = firstRun.current;
    firstRun.current = false;
    if (serverError != null) {
      applyFailure(serverError, initial);
    }
  }, [serverError, applyFailure]);

  const submit = useCallback(
    (submitted: Record<string, unknown>): void => {
      holdFocus();
      setBusy(true);
      setFailure(undefined);
      Promise.resolve()
        .then(() => latest.current.onSubmit(submitted))
        .then(result => {
          if (!isSubmitFailure(result)) {
            clearCardDraft(token);
            return;
          }
          applyFailure(
            {
              message: result.message,
              fieldErrors: result.fieldErrors,
              values: submitted,
            },
            false,
          );
        })
        .catch(() => {
          setBusy(false);
          setFailure(plainFailure(submitFailedMessage, 'submit'));
        });
    },
    [token, holdFocus, applyFailure, submitFailedMessage],
  );

  const cancel = useCallback((): void => {
    holdFocus();
    setCancelling(true);
    Promise.resolve()
      .then(() => latest.current.onCancel())
      .then(() => {
        clearCardDraft(token);
        setCancelling(false);
      })
      .catch(() => {
        setCancelling(false);
        setFailure(plainFailure(cancelFailedMessage, 'cancel'));
      });
  }, [token, holdFocus, cancelFailedMessage]);

  const markEdited = useCallback((key: string): void => {
    setEdited(previous => addOnce(previous, key));
  }, []);

  const stillApplies = (
    key: string,
    rejected?: Record<string, unknown>,
  ): boolean =>
    rejected == null
      ? !edited.some(editedKey => belongsTo(key, editedKey))
      : rejectedValueHolds(key, rejected, values, fields);

  const serverFieldErrors: FieldErrors =
    failure == null
      ? {}
      : Object.fromEntries(
          Object.entries(failure.fieldErrors).filter(([key]) =>
            stillApplies(key, failure.rejected),
          ),
        );

  return {
    busy,
    cancelling,
    failure,
    serverFieldErrors,
    markEdited,
    submit,
    cancel,
  };
};

export const CardFailureAlert = ({
  failure,
  busy,
  canRetrySubmit,
  onRetrySubmit,
  onRetryCancel,
}: {
  failure?: CardFailure;
  busy: boolean;
  canRetrySubmit: boolean;
  onRetrySubmit: () => void;
  onRetryCancel: () => void;
}): JSX.Element | null => {
  if (failure == null) {
    return null;
  }
  const retryCancel = failure.retry === 'cancel';
  const showRetry =
    !busy && (retryCancel || (failure.retry === 'submit' && canRetrySubmit));
  return (
    <Alert
      severity="error"
      action={
        showRetry ? (
          <LinkButton
            type="button"
            onClick={retryCancel ? onRetryCancel : onRetrySubmit}
          >
            Try again
          </LinkButton>
        ) : undefined
      }
    >
      {failure.message}
      {failure.unmapped.map(([key, message]) => (
        <AlertLine key={key}>{message}</AlertLine>
      ))}
    </Alert>
  );
};
